import { Dispatch } from 'redux'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks'
import { loadAllCompanies } from 'app/state/ducks/companies/thunks'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { loadCalendars } from 'app/state/ducks/holiday-calendars/thunks'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'

/**
 * The reference lists a form reads from the store, loaded when that form
 * opens rather than when the page it lives on arrives (ADR_LOS-030, phase 3:
 * a grid page makes one read). Every loader is coalesced, so a form opened
 * twice, or two forms sharing a list, costs one request per list.
 */

type Thunk = (dispatch: Dispatch) => Promise<unknown>

const all = (thunks: Thunk[]) => (dispatch: Dispatch) =>
  Promise.all(thunks.map(thunk => thunk(dispatch)))

/** Entities with their profiles, contacts and holiday calendars: what the deal form offers in its dropdowns. */
export const loadDealFormLists = () =>
  all([
    loadEntities() as Thunk,
    loadAllEntityProfiles() as Thunk,
    loadContacts() as Thunk,
    loadCalendars() as Thunk,
  ])

/**
 * The deal form's lists plus the deals to attach to, the facilities already
 * drawn on a deal (its availability check), the index rate options and the
 * wire instructions.
 */
export const loadFacilityFormLists = () =>
  all([
    loadEntities() as Thunk,
    loadAllEntityProfiles() as Thunk,
    loadContacts() as Thunk,
    loadCalendars() as Thunk,
    loadAllDeals() as Thunk,
    loadAllFacilities() as Thunk,
    loadIndexRateOptions() as Thunk,
    loadWireInstructions() as Thunk,
  ])

/**
 * Entities, wire instructions and index rate options, plus the deals: the
 * loan stepper finds the facility's deal in the deal store (its holiday
 * calendars and pricing-grid flag come from it).
 */
export const loadLoanFormLists = () =>
  all([
    loadEntities() as Thunk,
    loadWireInstructions() as Thunk,
    loadIndexRateOptions() as Thunk,
    loadAllDeals() as Thunk,
  ])

/**
 * What the entity form reads: the entities (its parent / fund manager pickers
 * and third-party providers), the entity types and profiles its dropdowns
 * offer, and the companies it links to.
 */
export const loadEntityFormLists = () =>
  all([
    loadEntities() as Thunk,
    loadAllEntityTypes() as Thunk,
    loadAllEntityProfiles() as Thunk,
    loadAllCompanies() as Thunk,
  ])

/**
 * The entity form's lists plus the contacts and wire instructions: editing an
 * entity prefills its contact and wire steps with the ones it already has.
 */
export const loadEntityEditLists = () =>
  all([
    loadEntities() as Thunk,
    loadAllEntityTypes() as Thunk,
    loadAllEntityProfiles() as Thunk,
    loadAllCompanies() as Thunk,
    loadContacts() as Thunk,
    loadWireInstructions() as Thunk,
  ])
